import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuthContext, apiClient } from '../App';

const NotificationBell = ({ onNavigate, desktopOnly = false }) => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [desktop, setDesktop] = useState(() => window.matchMedia?.('(min-width: 640px)').matches ?? true);
  const active = !desktopOnly || desktop;

  useEffect(() => {
    if (!desktopOnly) return undefined;
    const query = window.matchMedia('(min-width: 640px)');
    const change = () => setDesktop(query.matches);
    query.addEventListener('change', change);
    return () => query.removeEventListener('change', change);
  }, [desktopOnly]);

  useEffect(() => {
    if (!token || !active) return;

    const fetchUnreadCount = async () => {
      try {
        const res = await apiClient.get('/notifications/unread-count');
        setUnreadCount(res.data.count);
      } catch (err) {
        console.error('Failed to fetch notification count:', err);
      }
    };

    // Fetch immediately
    fetchUnreadCount();

    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [token, active]);

  if (!token || !active) return null;

  return (
    <Button
      variant="ghost"
      onClick={() => (onNavigate || navigate)('/notifications')}
      aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
      className="h-10 px-3 text-[14px] font-medium text-[#52525B] hover:text-[#111111] hover:bg-[#FAFAFA] rounded-[12px] gap-1 relative"
      data-testid="notification-bell"
    >
      <Bell className="w-4 h-4" />
      {unreadCount > 0 && (
        <span 
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-orange-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center px-1"
          data-testid="notification-count"
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
      <span className="hidden sm:inline">Inbox</span>
    </Button>
  );
};

export default NotificationBell;
